import React from 'react';
import Chart from 'react-apexcharts';
import { formatSastDateKey, formatSastWeekRange } from '../utils/sastDates';

const ValidatorAnalyticsTabs = ({ 
  activeTab, 
  analyticsData, 
  analyticsLoading, 
  selectedModerator, 
  allModerators 
}) => {
  
  // Debug logging removed to prevent render loops
  
  // Workload & Efficiency Tab
  if (activeTab === 'workload') {
    return (
      <div>
        {analyticsLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
            <p>Loading workload analysis...</p>
          </div>
        ) : (
          <div>
            <h5 style={{ margin: '0 0 25px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
              Workload & Efficiency Analysis
            </h5>
            
            {/* Efficiency Metrics (moved to top) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-tachometer-alt" style={{ marginRight: '8px', color: '#fd7e14' }}></i>
                Efficiency Metrics
              </h6>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                {/* Peak Hour Volume */}
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8', marginBottom: '5px' }}>
                    {analyticsData.workloadData?.hourlyActivity?.length > 0 ? 
                      Math.max(...analyticsData.workloadData.hourlyActivity.map(h => h.count || 0)) : 0}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Peak Hour Volume</div>
                </div>
                {/* Most Active Hour */}
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>
                    {analyticsData.workloadData?.hourlyActivity?.length > 0 ? 
                      `${(analyticsData.workloadData.hourlyActivity.reduce((best, h) => (h.count > (best?.count || 0) ? h : best), null)?.label) || 'N/A'}` : 'N/A'}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Most Active Hour</div>
                </div>
                {/* Most Active Day */}
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#6f42c1', marginBottom: '5px' }}>
                    {analyticsData.workloadData?.dayOfWeekActivity?.length > 0 ?
                      `${(analyticsData.workloadData.dayOfWeekActivity.reduce((best, d) => (d.count > (best?.count || 0) ? d : best), null)?.label) || 'N/A'}` : 'N/A'}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Most Active Day</div>
                </div>
                {/* Avg Validations per Day */}
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14', marginBottom: '5px' }}>
                    {analyticsData.dailyTrends?.length > 0 ? 
                      Math.round(
                        (analyticsData.dailyTrends.reduce((sum, d) => sum + (d.slipsValidated || 0), 0) / analyticsData.dailyTrends.length)
                      ) : 0}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px' }}>Avg Per Day</div>
                </div>
              </div>
            </div>

            {/* Daily Validation Activity (moved from Daily Trends) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-chart-line" style={{ marginRight: '8px', color: '#007bff' }}></i>
                Daily Validation Activity
              </h6>
              {analyticsData.dailyTrends?.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={{
                      chart: { type: 'line', height: 300, background: '#FFFFFF' },
                      xaxis: { 
                        categories: analyticsData.dailyTrends.map(d => formatSastDateKey(d.date)),
                        title: { text: 'Date (SAST)' },
                        labels: { rotate: -45 }
                      },
                      yaxis: { title: { text: 'Number of Validations' }, min: 0 },
                      colors: ['#007bff'],
                      title: { text: 'Daily Validation Activity (moderation time, SAST)' },
                      stroke: { curve: 'smooth', width: 3 },
                      markers: { size: 4 }
                    }}
                    series={[{
                      name: 'Validations Completed',
                      data: analyticsData.dailyTrends.map(d => d.slipsValidated || 0)
                    }]}
                    type="line"
                    height={300}
                  />
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No daily data available</p>
              )}
            </div>

            {/* Day of Week Pattern */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-calendar-alt" style={{ marginRight: '8px', color: '#6f42c1' }}></i>
                Day of Week Activity
              </h6>
              {analyticsData.workloadData?.dayOfWeekActivity?.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={{
                      chart: { type: 'bar', height: 300, background: '#FFFFFF', animations: { enabled: false } },
                      xaxis: { 
                        categories: analyticsData.workloadData.dayOfWeekActivity.map(d => d.label || 'N/A'),
                        title: { text: 'Day of Week' }
                      },
                      yaxis: { title: { text: 'Number of Validations' }, min: 0 },
                      colors: ['#6f42c1'],
                      title: { text: 'Validation Activity by Day of Week (moderation time, SAST)' },
                      plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
                      dataLabels: { enabled: false }
                    }}
                    series={[{
                      name: 'Validations',
                      data: analyticsData.workloadData.dayOfWeekActivity.map(d => d.count || 0)
                    }]}
                    type="bar"
                    height={300}
                  />
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No day-of-week data available</p>
              )}
            </div>

            {/* Weekly Activity Overview (moved from Daily Trends) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-calendar-week" style={{ marginRight: '8px', color: '#17a2b8' }}></i>
                Weekly Activity Overview
              </h6>
              {analyticsData.weeklyTrends?.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={{
                      chart: { type: 'bar', height: 300, background: '#FFFFFF' },
                      xaxis: { 
                        categories: analyticsData.weeklyTrends?.map(w => formatSastWeekRange(w.weekStart)),
                        title: { text: 'Week' },
                        labels: { rotate: -45 }
                      },
                      yaxis: { title: { text: 'Number of Validations' }, min: 0 },
                      colors: ['#17a2b8'],
                      title: { text: 'Weekly Validation Volume (moderation time, SAST)' },
                      plotOptions: { bar: { columnWidth: '60%', borderRadius: 4 } },
                      dataLabels: { enabled: false }
                    }}
                    series={[{
                      name: 'Validations Completed',
                      data: analyticsData.weeklyTrends?.map(w => w.slipsValidated || 0) || [0]
                    }]}
                    type="bar"
                    height={300}
                  />
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No weekly data available</p>
              )}
            </div>

            {/* Hourly Activity Pattern */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-clock" style={{ marginRight: '8px', color: '#17a2b8' }}></i>
                Hourly Activity Pattern
              </h6>
              {analyticsData.workloadData?.hourlyActivity?.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={{
                      chart: { type: 'area', height: 300, background: '#FFFFFF' },
                      xaxis: { 
                        categories: analyticsData.workloadData?.hourlyActivity?.map(h => h.label || 'N/A') || ['No Data'],
                        title: { text: 'Hour of Day (SAST)' }
                      },
                      yaxis: { title: { text: 'Number of Validations' } },
                      colors: ['#17a2b8'],
                      title: { text: 'Validation Activity Throughout the Day (by moderation time, SAST)' },
                      fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 } }
                    }}
                    series={[{
                      name: 'Validations',
                      data: analyticsData.workloadData?.hourlyActivity?.map(h => h.count || 0) || [0]
                    }]}
                    type="area"
                    height={300}
                  />
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No hourly data available</p>
              )}
            </div>

            
          </div>
        )}
      </div>
    );
  }

  // Quality Insights Tab
  if (activeTab === 'quality') {
    return (
      <div>
        {analyticsLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
            <p>Loading quality insights...</p>
          </div>
        ) : (
          <div>
            <h5 style={{ margin: '0 0 25px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
              Quality Insights & Performance Analysis
            </h5>
            {/* Rolling Daily Accuracy (moved to top) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-wave-square" style={{ marginRight: '8px', color: '#6c757d' }}></i>
                Rolling Daily Accuracy (7-day)
              </h6>
              {analyticsData.dailyTrends?.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={{
                      chart: { type: 'line', height: 300, background: '#FFFFFF' },
                      xaxis: { 
                        categories: analyticsData.dailyTrends.map(d => formatSastDateKey(d.date)),
                        title: { text: 'Date (SAST)' },
                        labels: { rotate: -45 }
                      },
                      yaxis: { title: { text: 'Rolling Accuracy (%)' }, min: 0, max: 100 },
                      colors: ['#6c757d'],
                      title: { text: '7-Day Rolling Accuracy' },
                      stroke: { curve: 'smooth', width: 3 },
                      markers: { size: 4 }
                    }}
                    series={[{
                      name: '7-Day Avg (%)',
                      data: (() => {
                        const values = analyticsData.dailyTrends.map(d => parseFloat((d.accuracy || 0).toFixed(1)) || 0);
                        const window = 7;
                        return values.map((_, i) => {
                          const start = Math.max(0, i - (window - 1));
                          const slice = values.slice(start, i + 1);
                          const avg = slice.reduce((s, v) => s + v, 0) / (slice.length || 1);
                          return parseFloat(avg.toFixed(1));
                        });
                      })()
                    }]}
                    type="line"
                    height={300}
                  />
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No rolling accuracy data available</p>
              )}
            </div>
            
            {/* Quality Trends (30-Day) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-chart-line" style={{ marginRight: '8px', color: '#17a2b8' }}></i>
                Accuracy over the last 30 days
              </h6>
              {analyticsData.dailyTrends?.length > 0 ? (
                <div className="chart-container">
                  <Chart
                    options={{
                      chart: { type: 'line', height: 300, background: '#FFFFFF' },
                      xaxis: { 
                        categories: analyticsData.dailyTrends?.slice(-30)?.map(d => formatSastDateKey(d.date)) || ['No Data'],
                        title: { text: 'Date (SAST)' }
                      },
                      yaxis: { title: { text: 'Accuracy (%)' }, min: 0, max: 100 },
                      colors: ['#17a2b8'],
                      title: { text: '30-Day Rolling Accuracy Trend' },
                      stroke: { curve: 'smooth', width: 3 },
                      markers: { size: 4 }
                    }}
                    series={[{
                      name: 'Rolling Accuracy (%)',
                      data: analyticsData.dailyTrends?.slice(-30)?.map(d => parseFloat((d.accuracy || 0).toFixed(1))) || [0]
                    }]}
                    type="line"
                    height={300}
                  />
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No trend data available</p>
              )}
            </div>

            {/* Rolling Daily Accuracy was moved to top */}

            {/* Weekly Accuracy Trend hidden */}

            {/* Retailer-Specific Performance (ordered by total validations) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-store" style={{ marginRight: '8px', color: '#6f42c1' }}></i>
                Performance by Retailer (Top 10)
              </h6>
              {analyticsData.retailerPerformance?.length > 0 ? (
                <div>
                  <div className="chart-container" style={{ marginBottom: '20px' }}>
                    <Chart
                      options={{
                        chart: { type: 'bar', height: 400, background: '#FFFFFF' },
                        xaxis: { 
                          categories: analyticsData.retailerPerformance?.slice()?.sort((a,b) => (b.total||0) - (a.total||0))?.slice(0, 10)?.map(r => r.retailer || 'Unknown') || ['No Data'],
                          title: { text: 'Retailer' }
                        },
                        yaxis: { title: { text: 'Number of Validations' } },
                        colors: ['#28a745', '#6f42c1'],
                        title: { text: 'Total Validations vs Correct Validations (Top 10 Retailers)' },
                        legend: { position: 'top' }
                      }}
                      series={[
                        {
                          name: 'Correct Validations',
                          data: analyticsData.retailerPerformance?.slice()?.sort((a,b) => (b.total||0) - (a.total||0))?.slice(0, 10)?.map(r => r.correct || 0) || [0]
                        },
                        {
                          name: 'Total Validations',
                          data: analyticsData.retailerPerformance?.slice()?.sort((a,b) => (b.total||0) - (a.total||0))?.slice(0, 10)?.map(r => r.total || 0) || [0]
                        }
                      ]}
                      type="bar"
                      height={400}
                    />
                  </div>
                  
                  {/* Retailer Performance Table */}
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                          <th style={{ padding: '12px', textAlign: 'left', fontSize: '14px', fontWeight: '600', color: '#333' }}>Retailer</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#333' }}>Total Validations</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#333' }}>Correct</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#333' }}>Errors</th>
                          <th style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600', color: '#333' }}>Accuracy</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analyticsData.retailerPerformance?.slice()?.sort((a,b) => (b.total||0) - (a.total||0))?.map((retailer, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                            <td style={{ padding: '12px', fontSize: '14px' }}>{retailer.retailer}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px' }}>{retailer.total}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', color: '#28a745' }}>{retailer.correct}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', color: '#dc3545' }}>{retailer.errors}</td>
                            <td style={{ padding: '12px', textAlign: 'right', fontSize: '14px', fontWeight: '600' }}>
                              <span style={{ 
                                color: retailer.accuracy >= 95 ? '#28a745' : retailer.accuracy >= 90 ? '#fd7e14' : '#dc3545',
                                backgroundColor: retailer.accuracy >= 95 ? '#d4edda' : retailer.accuracy >= 90 ? '#fff3cd' : '#f8d7da',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '12px'
                              }}>
                                {retailer.accuracy.toFixed(1)}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No retailer performance data available</p>
              )}
            </div>

          </div>
        )}
      </div>
    );
  }

  // Coaching Opportunities Tab
  if (activeTab === 'coaching') {
    return (
      <div>
        {analyticsLoading ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
            <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
            <p>Loading coaching insights...</p>
          </div>
        ) : (
          <div>
            <h5 style={{ margin: '0 0 25px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
              Coaching Opportunities & Development
            </h5>
            
            {/* Peer Comparison */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-users" style={{ marginRight: '8px', color: '#007bff' }}></i>
                Peer Comparison
              </h6>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff', marginBottom: '5px' }}>
                    {selectedModerator?.accuracy?.toFixed(1) || '0.0'}%
                  </div>
                  <div style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>Your Accuracy</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedModerator?.accuracy >= 95 ? '🏆 Top Performer' : 
                     selectedModerator?.accuracy >= 90 ? '⭐ Above Average' : 
                     selectedModerator?.accuracy >= 80 ? '📈 Room for Improvement' : '🎯 Focus Area'}
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>
                    {allModerators?.length > 0 ? 
                      (allModerators.reduce((sum, m) => sum + m.accuracy, 0) / allModerators.length).toFixed(1) : '0.0'}%
                  </div>
                  <div style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>Team Average</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {selectedModerator?.accuracy >= (allModerators?.reduce((sum, m) => sum + m.accuracy, 0) / allModerators?.length || 0) ? 
                      '👆 Above Average' : '👇 Below Average'}
                  </div>
                </div>
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#fd7e14', marginBottom: '5px' }}>
                    {(() => {
                      // Sort moderators by accuracy (descending) to get proper ranking
                      const sortedModerators = [...(allModerators || [])].sort((a, b) => (b.accuracy || 0) - (a.accuracy || 0));
                      const rank = sortedModerators.findIndex(m => m.name === selectedModerator?.name);
                      // If found, add 1 for 1-based ranking; if not found, show N/A
                      return rank >= 0 ? `#${rank + 1}` : 'N/A';
                    })()}
                  </div>
                  <div style={{ color: '#666', fontSize: '14px', marginBottom: '10px' }}>Team Ranking</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    of {allModerators?.length || 0} validators
                  </div>
                </div>
              </div>
            </div>

            {/* Improvement Suggestions (moved before Common Mistakes) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-lightbulb" style={{ marginRight: '8px', color: '#ffc107' }}></i>
                Personalized Improvement Suggestions
              </h6>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                {(() => {
                  const suggestions = [];
                  
                  if (selectedModerator?.accuracy < 90) {
                    suggestions.push({
                      icon: '🎯',
                      title: 'Focus on Accuracy',
                      description: 'Your accuracy is below the 90% target. Review common error patterns and take time to double-check calculations.',
                      priority: 'high'
                    });
                  }
                  
                  if (analyticsData.errorPatterns?.length > 0) {
                    const topError = analyticsData.errorPatterns[0];
                    suggestions.push({
                      icon: '📚',
                      title: `Address ${topError.type} Errors`,
                      description: `You have ${topError.count} ${topError.type} errors. Consider additional training in this area.`,
                      priority: 'medium'
                    });
                  }
                  
                  if (analyticsData.retailerPerformance?.length > 0) {
                    const worstRetailer = analyticsData.retailerPerformance[analyticsData.retailerPerformance.length - 1];
                    if (worstRetailer.accuracy < 85) {
                      suggestions.push({
                        icon: '🏪',
                        title: `Improve ${worstRetailer.retailer} Validations`,
                        description: `Your accuracy with ${worstRetailer.retailer} is ${worstRetailer.accuracy.toFixed(1)}%. Review their specific campaign rules.`,
                        priority: 'medium'
                      });
                    }
                  }
                  
                  suggestions.push({
                    icon: '📈',
                    title: 'Track Daily Progress',
                    description: 'Monitor your daily accuracy trends to identify patterns and maintain consistent performance.',
                    priority: 'low'
                  });
                  
                  return suggestions.map((suggestion, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'flex-start',
                      padding: '15px',
                      border: `1px solid ${suggestion.priority === 'high' ? '#dc3545' : suggestion.priority === 'medium' ? '#fd7e14' : '#28a745'}`,
                      borderRadius: '8px',
                      backgroundColor: suggestion.priority === 'high' ? '#fff5f5' : suggestion.priority === 'medium' ? '#fff8f0' : '#f0fff4'
                    }}>
                      <div style={{ fontSize: '24px', marginRight: '15px' }}>{suggestion.icon}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '5px' }}>
                          {suggestion.title}
                        </div>
                        <div style={{ fontSize: '13px', color: '#666', lineHeight: '1.4' }}>
                          {suggestion.description}
                        </div>
                      </div>
                      <div style={{ 
                        fontSize: '10px', 
                        fontWeight: '600', 
                        color: suggestion.priority === 'high' ? '#dc3545' : suggestion.priority === 'medium' ? '#fd7e14' : '#28a745',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {suggestion.priority}
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Common Mistakes (moved below Improvement Suggestions) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
              <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                <i className="fas fa-exclamation-circle" style={{ marginRight: '8px', color: '#dc3545' }}></i>
                Most Common Mistakes
              </h6>
              {analyticsData.errorPatterns?.length > 0 ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
                  {analyticsData.errorPatterns.slice(0, 5).map((error, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '15px',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      backgroundColor: index === 0 ? '#fff5f5' : '#ffffff'
                    }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#333', marginBottom: '5px' }}>
                          {error.type}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          Avg Impact: R{error.avgAmount?.toFixed(2) || '0.00'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#dc3545' }}>
                          {error.count}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          occurrences
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No error data available for analysis</p>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Default case - tab not implemented yet
  return (
    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
      <i className="fas fa-chart-line" style={{ fontSize: '48px', marginBottom: '20px', color: '#007bff' }}></i>
      <h6 style={{ margin: '0 0 15px 0', color: '#333' }}>
        Analytics Tab: {activeTab}
      </h6>
      <p style={{ margin: '0', fontSize: '14px' }}>
        Coming soon - detailed analytics will be displayed here
      </p>
    </div>
  );
};

export default ValidatorAnalyticsTabs;
